import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useLiveVersion } from '../../shared/live';
import { MyAssignments } from '../assignments/MyServicePage';
import { useMe } from '../session/SessionProvider';

export type InterestStatus = 'revision' | 'mas_adelante' | 'aceptado';
type Ref = { personId: string; name: string };
export type Position = {
  id: string;
  title: string;
  description: string | null;
  ministry: { id: string; name: string };
  open: boolean;
  createdAt: string;
  myStatus: InterestStatus | null;
  contact: (Ref & { chatId: string }) | null;
  canManage: boolean;
  interested: { person: Ref; status: InterestStatus; at: string }[] | null;
  interestedCount: number;
};
type Stats = { canSee: boolean; total: number; items: { ministry: { id: string; name: string }; count: number; people: Ref[] }[] };
type ServeProfile = { profile: { interests: string[] }; options: { ministries: { id: string; name: string }[] } };
type Overview = { canPost: boolean; ministries: { id: string; name: string }[]; vocationMinistries: { id: string; name: string }[]; items: Position[] };

export const INTEREST_LABEL: Record<InterestStatus, string> = { revision: 'En revisión', mas_adelante: 'Más adelante', aceptado: '¡Fuiste aceptado!' };
const BADGE: Record<InterestStatus, string> = { revision: 'badge--pending', mas_adelante: '', aceptado: 'badge--active' };

/** Mi Vocación: vacantes de los ministerios de vocación, «Me interesa» y, para sus líderes, quién se interesó. */
export function VocationPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [params, setParams] = useSearchParams();
  // «Publicar vacante» es un botón junto al título; ?ver=publicar (dirección de antes) lo abre.
  const [posting, setPosting] = useState(params.get('ver') === 'publicar');
  const base = `/organization/churches/${churchId}/vocation`;
  const live = useLiveVersion((e) => e.kind.startsWith('vocation'));
  const data = useAsync((signal) => (churchId ? api.get<Overview>(base, signal) : Promise.resolve(null)), [base, live]);
  const stats = useAsync((signal) => (churchId ? api.get<Stats>(`/assignments/churches/${churchId}/serve-stats`, signal).catch(() => null) : Promise.resolve(null)), [churchId]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const o = data.data;
  if (!o) return null;

  const canStats = stats.status === 'ready' && stats.data?.canSee === true;
  const tabs: [string, string][] = [
    ['vacantes', 'Vacantes'],
    ['mias', 'Mis intereses'],
    ['asignaciones', 'Mis asignaciones'],
    ...(canStats ? ([['estadistica', 'Estadística']] as [string, string][]) : []),
  ];
  const tab = tabs.find(([k]) => k === params.get('ver'))?.[0] ?? 'vacantes';
  const open = o.items.filter((p) => p.open);
  const mine = o.items.filter((p) => p.myStatus);

  async function run(key: string, action: () => Promise<unknown>, ok: string) {
    setBusy(key);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <div>
          <h1>Mi Vocación</h1>
          <p className="muted">Descubre dónde servir: los ministerios publican sus vacantes y tú dices «Me interesa».</p>
        </div>
        {o.canPost && !posting ? <Button onClick={() => setPosting(true)}>Publicar vacante</Button> : null}
      </header>
      {posting && o.canPost ? (
        <NewPosition
          base={base}
          ministries={o.ministries}
          onDone={() => {
            data.reload();
            setPosting(false);
            setParams({}, { replace: true });
            setMsg({ tone: 'success', text: 'Vacante publicada.' });
          }}
          onCancel={() => setPosting(false)}
        />
      ) : null}
      <div className="tabs tabs--scroll" role="tablist" aria-label="Mi Vocación">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams(k === 'vacantes' ? {} : { ver: k }, { replace: true })}>
            {label}
          </button>
        ))}
      </div>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}

      {tab === 'vacantes' ? (
        open.length === 0 ? (
          <Card>
            <EmptyState title="Por ahora no hay vacantes abiertas.">
              {o.vocationMinistries.length === 0 ? <p className="muted small">Cuando la administración marque ministerios de vocación, aquí aparecerán sus vacantes.</p> : null}
            </EmptyState>
          </Card>
        ) : (
          open.map((p) => (
            <Card key={p.id} title={p.title}>
              <p className="small muted">{p.ministry.name}</p>
              {p.description ? <p className="pre-line">{p.description}</p> : null}
              {p.myStatus ? (
                <p>
                  <span className={`badge ${BADGE[p.myStatus]}`}>{INTEREST_LABEL[p.myStatus]}</span>{' '}
                  {p.myStatus === 'revision' ? (
                    <Button variant="ghost" busy={busy === `w${p.id}`} onClick={() => run(`w${p.id}`, () => api.delete(`${base}/positions/${p.id}/interest`), 'Quitaste tu interés.')}>
                      Ya no me interesa
                    </Button>
                  ) : null}
                </p>
              ) : (
                <Button busy={busy === `i${p.id}`} onClick={() => run(`i${p.id}`, () => api.post(`${base}/positions/${p.id}/interest`), '¡Listo! Tu interés quedó en revisión con el líder del ministerio.')}>
                  Me interesa
                </Button>
              )}
              {p.canManage ? <ManagePosition base={base} position={p} busy={busy} run={run} /> : null}
            </Card>
          ))
        )
      ) : null}

      {tab === 'mias' && churchId ? <MinistryWishes churchId={churchId} onSaved={stats.reload} /> : null}
      {tab === 'estadistica' && canStats && stats.status === 'ready' && stats.data ? <StatsTab stats={stats.data} /> : null}
      {tab === 'mias' ? (
        mine.length === 0 ? (
          <Card>
            <EmptyState title="Todavía no te has interesado en ninguna vacante.">
              <Link to="/app/vocacion">Ver vacantes</Link>
            </EmptyState>
          </Card>
        ) : (
          <ul className="list" aria-label="Mis intereses">
            {mine.map((p) => (
              <li key={p.id} className="list__item">
                <span>
                  <strong>{p.title}</strong> <span className="small muted">· {p.ministry.name}</span>
                  {p.myStatus === 'aceptado' && p.contact ? (
                    <span className="block small">
                      Acércate con <strong>{p.contact.name}</strong> ·{' '}
                      <Link to={`/app/chats/${p.contact.chatId}`}>Ir al chat del ministerio</Link>
                    </span>
                  ) : null}
                  {p.myStatus === 'mas_adelante' ? <span className="block small muted">Esta vez se eligió a otra persona. Tu interés queda guardado.</span> : null}
                </span>
                <span className={`badge ${BADGE[p.myStatus!]}`}>{INTEREST_LABEL[p.myStatus!]}</span>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {tab === 'asignaciones' && churchId ? <MyAssignments churchId={churchId} /> : null}

      {tab === 'vacantes'
        ? o.items
            .filter((p) => !p.open && p.canManage)
            .slice(0, 10)
            .map((p) => (
              <Card key={p.id} title={`${p.title} (cerrada)`}>
                <p className="small muted">{p.ministry.name}</p>
                {p.interested?.find((i) => i.status === 'aceptado') ? <p className="small">Aceptado: <strong>{p.interested.find((i) => i.status === 'aceptado')!.person.name}</strong></p> : <p className="small muted">Se cerró sin elegir a nadie.</p>}
              </Card>
            ))
        : null}
    </div>
  );
}

function ManagePosition({ base, position: p, busy, run }: { base: string; position: Position; busy: string | null; run: (key: string, action: () => Promise<unknown>, ok: string) => void }) {
  const waiting = (p.interested ?? []).filter((i) => i.status === 'revision');
  return (
    <div className="vocation-manage">
      <h3 className="small">Interesados ({waiting.length})</h3>
      {waiting.length === 0 ? <p className="small muted">Aún nadie se interesa.</p> : null}
      <ul className="list">
        {waiting.map((i) => (
          <li key={i.person.personId} className="list__item">
            <span>{i.person.name}</span>
            <Button
              variant="secondary"
              busy={busy === `a${p.id}${i.person.personId}`}
              onClick={() => {
                if (window.confirm(`¿Aceptar a ${i.person.name} en «${p.title}»? La vacante se cerrará y los demás quedarán en «Más adelante».`)) {
                  run(`a${p.id}${i.person.personId}`, () => api.post(`${base}/positions/${p.id}/accept`, { personId: i.person.personId }), `${i.person.name} fue aceptado. Ya le avisamos que se acerque contigo.`);
                }
              }}
            >
              Aceptar
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="ghost"
        busy={busy === `c${p.id}`}
        onClick={() => {
          if (window.confirm(`¿Cerrar «${p.title}» sin elegir a nadie?`)) run(`c${p.id}`, () => api.post(`${base}/positions/${p.id}/close`), 'Vacante cerrada.');
        }}
      >
        Cerrar vacante
      </Button>
    </div>
  );
}

function NewPosition({ base, ministries, onDone, onCancel }: { base: string; ministries: { id: string; name: string }[]; onDone: () => void; onCancel: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`${base}/positions`, { ministryId: String(f.get('ministryId')), title: String(f.get('title') ?? '').trim(), description: String(f.get('description') ?? '').trim() || null });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Publicar una vacante">
      <form className="form" onSubmit={submit}>
        {error ? <Alert>{error}</Alert> : null}
        <div className="field">
          <label htmlFor="voc-ministry">Ministerio</label>
          <select id="voc-ministry" name="ministryId" required defaultValue={ministries[0]?.id}>
            {ministries.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        <Field label="Nombre de la posición" name="title" required minLength={2} maxLength={120} placeholder="Ej. Guitarrista, Maestro de niños, Ujier" />
        <div className="field">
          <label htmlFor="voc-desc">Descripción</label>
          <textarea id="voc-desc" name="description" rows={3} maxLength={2000} placeholder="Qué hará, cuándo y qué se necesita." />
        </div>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Publicar
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** «¿En qué ministerios te gustaría servir?»: para la estadística del pastor y para que los líderes sepan. */
function MinistryWishes({ churchId, onSaved }: { churchId: string; onSaved: () => void }) {
  const data = useAsync((signal) => api.get<ServeProfile>(`/assignments/churches/${churchId}/serve-profile`, signal), [churchId]);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (data.status !== 'ready') return null;
  const chosen = picked ?? data.data.profile.interests;
  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await api.put(`/assignments/churches/${churchId}/serve-interests`, { interests: chosen });
      setMsg({ tone: 'success', text: 'Guardado. Los líderes de esos ministerios ya saben que te gustaría servir ahí.' });
      onSaved();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="¿A qué ministerios te gustaría pertenecer?">
      <p className="small muted">Marca los que quieras. Nos ayuda a conocer dónde le gustaría servir a la iglesia.</p>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <fieldset className="weekday-picks">
        <legend className="visually-hidden">Ministerios</legend>
        {data.data.options.ministries.map((m) => (
          <label key={m.id} className="check">
            <input type="checkbox" checked={chosen.includes(m.id)} onChange={(e) => setPicked(e.target.checked ? [...chosen, m.id] : chosen.filter((x) => x !== m.id))} /> {m.name}
          </label>
        ))}
      </fieldset>
      <Button busy={busy} onClick={() => void save()}>
        Guardar mis ministerios
      </Button>
    </Card>
  );
}

/** Estadística: cuántos quieren pertenecer a cada ministerio (el pastor ve todos; cada líder, los suyos). */
function StatsTab({ stats }: { stats: Stats }) {
  const max = Math.max(1, ...stats.items.map((i) => i.count));
  return (
    <Card title="¿Dónde le gustaría servir a la iglesia?">
      <p className="small muted">{stats.total} {stats.total === 1 ? 'persona ha marcado' : 'personas han marcado'} los ministerios que les interesan.</p>
      {stats.items.length === 0 ? <p className="muted">Todavía nadie marca sus ministerios.</p> : null}
      <ul className="stat-bars">
        {stats.items.map((i) => (
          <li key={i.ministry.id}>
            <details>
              <summary>
                <span className="stat-bars__label">{i.ministry.name}</span>
                <span className="stat-bars__bar" style={{ width: `${(i.count / max) * 100}%` }} aria-hidden="true" />
                <strong className="stat-bars__count">{i.count}</strong>
              </summary>
              <p className="small">{i.people.map((p) => p.name).join(', ')}</p>
            </details>
          </li>
        ))}
      </ul>
    </Card>
  );
}
